import * as React from 'react';
import { hasHiddenUnicodeCharacters } from '@web-config-app/core';

interface UsePlainTextControlParams {
  value: string;
  path: string;
  onChange: (path: string, nextValue: string | undefined) => void;
}

export const getPlainTextControlHandlers = ({
  value,
  path,
  onChange,
}: UsePlainTextControlParams) => {
  const hasHiddenCharacters = hasHiddenUnicodeCharacters(value);

  const handleChange = (newValue: string) => {
    if (newValue.length < 1) {
      onChange(path, undefined);
      return;
    }

    onChange(path, newValue);
  };

  const handleClear = () => {
    onChange(path, undefined);
  };

  const handleBlur = (rawValue: string) => {
    // Trim regular boundary whitespace, but keep hidden Unicode characters in case the user is trying to paste in hidden characters on the beginning or end of the value.
    const trimmedValue = rawValue.replace(
      /^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g,
      '',
    );

    if (trimmedValue.length < 1) {
      // Preserve hidden chars on blur so warning/removal UI can be shown.
      onChange(
        path,
        hasHiddenUnicodeCharacters(rawValue) ? rawValue : undefined,
      );
      return;
    }

    onChange(path, trimmedValue);
  };

  return {
    hasHiddenCharacters,
    handleChange,
    handleClear,
    handleBlur,
  };
};

export const usePlainTextControl = ({
  value,
  path,
  onChange,
}: UsePlainTextControlParams) =>
  React.useMemo(
    () =>
      getPlainTextControlHandlers({
        value,
        path,
        onChange,
      }),
    [value, path, onChange],
  );
